import { ApiClient } from "../api-client";
import { ApiClientError } from "../errors";
import schemaValidators from "./schema-validators";

type AssessmentInfo = {
	lms: "canvas" | "moodle" | "BlackboardLearn" | "desire2learn";
	hostname: string;
	lmsProvidedCourseId?: string;
	lmsProvidedAssessmentId: string;
};

export default class AuthenticationClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	isAuthenticated = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
				credentials: "include",
			});
			if (response.ok) {
				return true;
			}
			return false;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAccessCode = async (body: {
		assessmentInfo?: AssessmentInfo;
		thirdPartyUrl?: string;
		hasThirdPartyPassword?: boolean;
		target?: string;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/access-code`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(body),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.accessCode.validate(obj)) {
					return obj.code;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAccessControlAuthCode = async () => {
		try {
			const accountId = await this.getAccountId();
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/account/${accountId}/auth-code`,
				{
					method: "POST",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.accessControlAuthCode.validate(obj)) {
					return { accountId, authCode: obj.authCode };
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getRefreshTokenFromAuthCode = async (accountId: string, authCode: string) => {
		try {
			const response = await fetch(
				`${this.config.verityOrigin}/api/access-control/account/${accountId}/refresh-token`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						authCode,
					}),
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.refreshTokenInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	validateUserFromServer = async (suffix: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/validate/user?${suffix}`, {
				credentials: "include",
			});
			if (response.ok && response.status === 200) {
				return true;
			}
			return false;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
	};

	getAccount = async (accountId: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/account/${accountId}`);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.account.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	logout = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/end`, {
				credentials: "include",
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	logFailedAuth = async (message?: string) => {
		try {
			await super.fetch(`${this.config.verityOrigin}/api/verity/debug/auth`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					message: message,
				}),
			});
			return;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
	};

	getAccountPortals = async ({
		accountId,
		searchQuery,
		page,
		size,
	}: {
		accountId: string;
		searchQuery?: string;
		page: number;
		size: number;
	}) => {
		try {
			const queryParams = new URLSearchParams();
			if (searchQuery) queryParams.set("searchQuery", searchQuery);
			queryParams.set("page", page.toString());
			queryParams.set("size", size.toString());

			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/account/${accountId}/portals?${queryParams.toString()}`,
				{
					method: "GET",
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.getAccountPortals.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	setCurrentuserId = async ({ accountId, userId }: { accountId: string; userId: string }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/account/${accountId}/current-user/${userId}`,
				{
					method: "PUT",
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	mapNonLmsUser = async ({
		userUuid,
		courseUuid,
		instituteUuid,
	}: {
		userUuid: string;
		courseUuid: string;
		instituteUuid: string;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/portal/map`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					userUuid,
					courseUuid,
					instituteUuid,
				}),
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
